import { useState, useCallback, useDeferredValue, useEffect } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  DataGridPremium,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnGroupingModel,
  GridColumnResizeParams,
  GridEventListener,
  GridFeatureMode,
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridRowClassNameParams,
  GridRowModesModel,
  GridSortModel,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import CustomToolbar from '../atoms/CustomToolbar';
import { SxProps, Theme } from '@mui/material/styles';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { ReactI18NextChild } from 'react-i18next';
import debounce from 'lodash/debounce';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import noop from 'lodash/fp/noop';
import { Logger } from '../../services/serviceLogger';
import useLocalStorage from 'use-local-storage';
import map from 'lodash/fp/map';
import { frFR } from '@mui/x-data-grid-premium/locales';

const logger = Logger('DataGrid');

const setWidth = (columns: GridColDef[], size: Record<string, number>) => {
  return map((c) => {
    const width = size[c.field];
    return {
      ...c,
      width: width || c.width || undefined,
    };
  }, columns);
};

export type DataGridProps = {
  rows: any;
  columns: GridColDef[];
  pageSize?: number;
  sx?: SxProps<Theme>;
  getRowId?: (row: any) => string;
  hideFooterPagination: boolean;
  onCellDoubleClick?: (params: any) => void;
  getRowClassName: (params: GridRowClassNameParams) => string;
  handleRequestSort?: (e: any, prop: string, orderDirection: string) => void;
  getRows?: (v?: any) => void;
  sortingMode?: GridFeatureMode;
  filterMode?: GridFeatureMode;
  handleSearch?: (value: string, item?: GridFilterItem) => void;
  fetchLoading?: boolean;
  pagination?: boolean;
  editable?: boolean;
  processRowUpdate?: (updatedRow: any, originalRow: any) => void;
  onProcessRowUpdateError?: (error: any) => void;
  editMode?: 'row' | 'cell';
  rowModesModel?: GridRowModesModel;
  onRowModesModelChange?: (rowModesModel: GridRowModesModel, details: GridCallbackDetails<any>) => void;
  onRowEditStop?: GridEventListener<'rowEditStop'>;
  isCellEditable?: (params: GridCellParams) => boolean;
  childrenToolBar?: ReactI18NextChild | Iterable<ReactI18NextChild>;
  checkboxSelection?: boolean;
  apiRef?: React.MutableRefObject<GridApiPremium>;
  initialState?: GridInitialStatePremium;
  pinnedColumns?: { left?: Array<string>; right?: Array<string> };
  columnGroupingModel?: GridColumnGroupingModel;
  uniqueDatagridId: string;
};

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
  '& .MuiDataGrid-pinnedColumnHeaders': {
    backgroundColor: theme.palette.blue[60],
    color: 'white',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.blue[60],
    color: 'white',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.blue[60],
    color: 'white',
  },
  '& .header': {
    backgroundColor: theme.palette.blue[60],
    color: 'white',
  },
  '& .cellStyle': {
    borderLeft: '1px solid lightgrey',
  },
})) as typeof DataGridPremium;

const DataGrid = ({
  getRows,
  rows,
  columns: _columns,
  pageSize,
  sx,
  hideFooterPagination,
  getRowId,
  onCellDoubleClick,
  getRowClassName,
  handleRequestSort,
  handleSearch,
  sortingMode = 'client',
  filterMode = 'client',
  fetchLoading,
  pagination = false,
  processRowUpdate,
  onProcessRowUpdateError,
  editMode = 'cell',
  rowModesModel,
  onRowModesModelChange,
  onRowEditStop,
  isCellEditable,
  childrenToolBar,
  checkboxSelection,
  apiRef: apiRefExternal,
  initialState,
  pinnedColumns,
  columnGroupingModel,
  uniqueDatagridId,
}: DataGridProps) => {
  const [stateWidth, setStateWidth] = useLocalStorage<Record<string, number>>(
    `datagrid-filtermodel-${uniqueDatagridId}-columns`,
    {},
  );
  const defaultPageSize = pageSize || 50;
  const columns = setWidth(_columns, stateWidth);
  const [stateColumns, setStateColumns] = useState<GridColDef[]>(columns);

  const [filterModel, setFilterModel] = useLocalStorage<GridFilterModel | undefined>(
    `datagrid-filtermodel-${uniqueDatagridId}`,
    undefined,
  );

  const [sortModel, setSortModel] = useLocalStorage<GridSortModel | undefined>(
    `datagrid-sortmodel-${uniqueDatagridId}`,
    undefined,
  );

  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    localStorage.setItem(`datagrid-sortmodel-${uniqueDatagridId}`, JSON.stringify(model));
  };

  const [paginationModel, setPaginationModel] = useLocalStorage<GridPaginationModel | undefined>(
    `datagrid-paginationmodel-${uniqueDatagridId}`,
    {
      pageSize: defaultPageSize || 2,
      page: 0,
    },
  );

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
    localStorage.setItem(`datagrid-paginationmodel-${uniqueDatagridId}`, JSON.stringify(model));
  };

  const deferredstateColumns = useDeferredValue<GridColDef[]>(stateColumns);

  let apiRef = useGridApiRef();
  if (apiRefExternal) {
    apiRef = apiRefExternal;
  }

  useEffect(() => {
    setStateColumns(setWidth(_columns, stateWidth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_columns]);

  useEffect(() => {
    if (!apiRef.current) return;

    return apiRef.current.subscribeEvent('rowExpansionChange', (params) => {
      const haveSize = stateColumns.some((c) => c.field === '__row_group_by_columns_group__');
      if (params.childrenExpanded && !haveSize) {
        apiRef.current?.autosizeColumns({ includeOutliers: true }).catch((e) => logger.error(e));
      }
    });
  }, [apiRef, stateColumns]);

  useCallback(
    (sortModel: GridSortModel) => {
      if (handleRequestSort && sortModel[0] && sortModel[0].field && sortModel[0].sort)
        handleRequestSort(null, sortModel[0].field, sortModel[0].sort);
    },
    [handleRequestSort],
  );

  const onColumnResize = debounce((params: GridColumnResizeParams, event: MuiEvent, details: GridCallbackDetails) => {
    if (
      params.colDef.field === '__row_group_by_columns_group__' &&
      !stateColumns.some((c) => c.field === '__row_group_by_columns_group__')
    ) {
      setStateColumns((previousState) => {
        return [...previousState, params.colDef];
      });
    }

    setStateWidth((previousState) => {
      return { ...previousState, [params.colDef.field]: params.width };
    });
  }, 200);

  const onFilterChange = useCallback(
    (filterModelArgs: GridFilterModel) => {
      setFilterModel(filterModelArgs);
      if (handleSearch && filterModelArgs && filterModelArgs.items.length > 0) {
        for (const item of filterModelArgs.items) {
          handleSearch(item.value, item);
        }
      }
      if (getRows && handleSearch && filterModelArgs?.items?.length === 0) {
        getRows({
          variables: {
            reasearchField: '',
          },
        });
      }
    },
    [handleSearch, getRows, setFilterModel],
  );

  return (
    <Box>
      <StyledDataGrid
        apiRef={apiRef}
        sx={sx}
        showToolbar
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            children: childrenToolBar,
          },
        }}
        initialState={initialState}
        sortModel={sortModel}
        onCellClick={noop} // added to avoid double click to activate actions
        onCellDoubleClick={onCellDoubleClick}
        rows={rows}
        getRowId={getRowId}
        columns={deferredstateColumns}
        paginationModel={paginationModel}
        pageSizeOptions={[defaultPageSize, defaultPageSize * 2, defaultPageSize * 3]}
        onPaginationModelChange={onPaginationModelChange}
        density="compact"
        hideFooterPagination={hideFooterPagination}
        getRowClassName={getRowClassName}
        sortingMode={sortingMode}
        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
        filterMode={filterMode}
        onFilterModelChange={onFilterChange}
        pagination={pagination}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        editMode={editMode}
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        onRowEditStop={onRowEditStop}
        isCellEditable={isCellEditable}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={true}
        onColumnResize={onColumnResize}
        pinnedColumns={pinnedColumns}
        columnGroupingModel={columnGroupingModel}
        experimentalFeatures={{ columnGrouping: true }}
        filterModel={filterModel && filterModel?.items ? filterModel : undefined}
        onSortModelChange={onSortModelChange}
      />
    </Box>
  ) as React.ReactNode;
};

export default DataGrid;
export { DataGrid };
